/* BP-017 — Individual Acquisition Manager Scorecard.
   Hero, category breakdown, funnel, revenue, and history. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, RefreshCw, Download, Target, TrendingUp, Info, Sparkles, ThumbsUp, ThumbsDown, AlertTriangle, ArrowUp, ArrowDown, Minus, Activity } from "lucide-react";


const BAND_TW = {
  elite:              { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", bar: "bg-emerald-500" },
  strong:             { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200",     bar: "bg-sky-500" },
  developing:         { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   bar: "bg-amber-500" },
  needs_coaching:     { bg: "bg-orange-50",  text: "text-orange-700",  ring: "ring-orange-200",  bar: "bg-orange-500" },
  critical_attention: { bg: "bg-red-50",     text: "text-red-700",     ring: "ring-red-200",     bar: "bg-red-500" },
};

const CAT_ORDER = ["conversion", "revenue", "speed", "process", "deal_quality", "lead_stewardship"];


export default function ManagerScorecardPage() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [coaching, setCoaching] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [trends, setTrends] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h, t] = await Promise.all([
        api.get(`/v3/acquisition/scorecards/${id}`),
        api.get(`/v3/acquisition/scorecards/${id}/history`),
        api.get(`/v3/acquisition/scorecards/${id}/trends`).catch(() => null),
      ]);
      setCard(c.data);
      setHistory(h.data.items || []);
      if (t) setTrends(t.data);
      try {
        const co = await api.get(`/v3/acquisition/coaching/${id}/latest`);
        setCoaching(co.data);
      } catch { setCoaching(null); }
    } catch (err) {
      toast.error("Failed to load scorecard");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const recalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      await api.post("/v3/acquisition/scorecards/recalculate", {
        manager_ids: [id], reason: "manual" });
      toast.success("Recalculated");
      load();
    } catch {
      toast.error("Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  }, [id, load]);

  const exportPdf = useCallback(() => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/acquisition/exports/pdf?manager_id=${id}`,
      "_blank");
  }, [id]);

  const generateCoaching = useCallback(async (force = false) => {
    setCoachLoading(true);
    try {
      const { data } = await api.post(
        `/v3/acquisition/coaching/generate/${id}`, { force });
      setCoaching(data);
      toast.success(data.cached ? "Loaded cached coaching" : "AI coaching generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coaching generation failed");
    } finally {
      setCoachLoading(false);
    }
  }, [id]);

  const sendCoachFeedback = useCallback(async (rating) => {
    if (!coaching?.id) return;
    try {
      await api.post(
        `/v3/acquisition/coaching/${coaching.id}/feedback`, { rating });
      toast.success(`Feedback recorded: ${rating.replace("_", " ")}`);
    } catch (err) {
      toast.error("Feedback failed");
    }
  }, [coaching]);

  const band = useMemo(() =>
    card ? BAND_TW[card.band] || BAND_TW.developing : null, [card]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-600 text-sm"
             data-testid="mgr-loading">
        <Loader2 size={14} className="animate-spin"/> Loading…
      </div>);
  }
  if (!card) return <div className="p-6 text-red-600">Scorecard not found.</div>;

  const met = card.metrics || {};

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="mgr-page">
      {/* Breadcrumb */}
      <Link to="/v3/acquisition"
             className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
             data-testid="mgr-back">
        <ArrowLeft size={12}/> Back to leaderboard
      </Link>

      {/* Hero */}
      <div className={`rounded-xl border ${band.ring} ${band.bg} p-5`}
            data-testid="mgr-hero">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${band.text}`}>
              Overall Performance
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">
              {card.manager_name || card.manager_id}
            </h1>
            {card.manager_email && (
              <div className="text-xs text-slate-500">{card.manager_email}</div>
            )}
            <div className="mt-3 flex items-end gap-3">
              <div className="text-5xl font-bold tabular-nums text-slate-900"
                    data-testid="mgr-score">
                {card.overall_score.toFixed(1)}
              </div>
              <div className="text-xs text-slate-500 pb-1">/ 100</div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${band.text} bg-white/70 ring-1 ${band.ring}`}
                    data-testid="mgr-band">
                <span className={`inline-block h-2 w-2 rounded-full ${band.bar}`}/>
                {card.band_label}
              </span>
              {card.provisional && (
                <span className="text-[11px] font-bold uppercase text-amber-700 bg-amber-100 rounded-full px-2 py-0.5"
                      data-testid="mgr-provisional">
                  PROVISIONAL
                </span>
              )}
              <span className="text-[11px] text-slate-500">
                Confidence {(card.confidence * 100).toFixed(0)}%
              </span>
            </div>
            {card.provisional_reason && (
              <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
                <Info size={11}/> {card.provisional_reason}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={recalculate} disabled={recalculating}
                    data-testid="mgr-recalc"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
              {recalculating ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
              Recalculate
            </button>
            <button onClick={exportPdf} data-testid="mgr-pdf"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400">
              <Download size={12}/> PDF
            </button>
          </div>
        </div>
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="mgr-categories">
        {CAT_ORDER.map((catKey) => {
          const cat = card.categories?.[catKey];
          if (!cat) return null;
          return (
            <div key={catKey}
                  className="rounded-xl border border-slate-200 bg-white p-4"
                  data-testid={`mgr-cat-${catKey}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {cat.label}
                </div>
                <div className="text-[10px] text-slate-500">
                  Weight {(cat.weight * 100).toFixed(0)}%
                </div>
              </div>
              <div className="mt-2 flex items-end gap-2">
                <div className="text-3xl font-bold text-slate-900 tabular-nums">
                  {cat.score.toFixed(1)}
                </div>
                <div className="text-xs text-slate-500 pb-1">/ 100</div>
                <div className="ml-auto text-xs text-slate-500 tabular-nums">
                  +{cat.contribution.toFixed(2)}
                </div>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${band.bar}`}
                      style={{ width: `${Math.max(2, cat.score)}%` }}/>
              </div>
              {(cat.not_available || []).length > 0 && (
                <div className="mt-2 text-[10px] text-slate-500">
                  N/A: {cat.not_available.join(", ")}
                </div>
              )}
            </div>);
        })}
      </div>

      {/* Conversion funnel */}
      <div className="rounded-xl border border-slate-200 bg-white p-4"
            data-testid="mgr-funnel">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          Conversion Funnel — {card.period.start} → {card.period.end}
        </div>
        <FunnelRow label="New Leads Assigned" value={met.new_leads_assigned}/>
        <FunnelRow label="Two-Way Conversations" value={met.two_way_conversations}
                    reference={met.new_leads_assigned}/>
        <FunnelRow label="Appointments Set"    value={met.appointments_set}
                    reference={met.new_leads_assigned}/>
        <FunnelRow label="Offers Made"          value={met.offers_made}
                    reference={met.new_leads_assigned}/>
        <FunnelRow label="Contracts Signed"    value={met.contracts_signed}
                    reference={met.new_leads_assigned}/>
        <FunnelRow label="Closed Deals"         value={met.closed_deals}
                    reference={met.new_leads_assigned}/>
      </div>

      {/* Revenue + operational stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="mgr-revenue">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Revenue
          </div>
          <div className="text-3xl font-bold text-slate-900 tabular-nums">
            ${(met.gross_assignment_revenue || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Gross assignment revenue this period · {met.revenue_properties_count || 0} closed deals
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="mgr-ops">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Operational Metrics
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Stat label="Speed to 1st Contact"
                   value={met.speed_to_first_contact_min != null
                             ? `${met.speed_to_first_contact_min.toFixed(0)} min`
                             : "—"}/>
            <Stat label="Avg Deal Score"
                   value={met.avg_deal_score_at_contract || "—"}/>
            <Stat label="Follow-Up Compliance"
                   value={met.follow_up_compliance != null
                             ? `${(met.follow_up_compliance * 100).toFixed(0)}%`
                             : "—"}/>
            <Stat label="Lead Quality Index"
                   value={card.lead_quality_index || "—"}/>
          </div>
        </div>
      </div>

      {/* Coaching Trends — period-over-period deltas */}
      {trends && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="mgr-trends">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-slate-600"/>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                Coaching Trends
              </div>
              <span className="text-[10px] text-slate-500">
                · {trends.prev_period.start} → {trends.period.end}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="text-emerald-700">
                ↑ {trends.summary.improved} improved
              </span>
              <span className="text-red-700">
                ↓ {trends.summary.declined} declined
              </span>
              {trends.summary.overall_delta != null && (
                <span className={`font-semibold ${
                  trends.summary.overall_delta > 0 ? "text-emerald-700"
                  : trends.summary.overall_delta < 0 ? "text-red-700"
                  : "text-slate-500"}`}
                       data-testid="mgr-trends-overall">
                  Overall: {trends.summary.overall_delta > 0 ? "+" : ""}{(trends.summary.overall_delta * 100).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {trends.items.slice(0, 6).map((it) => (
              <TrendRow key={it.key} item={it}/>
            ))}
          </div>
        </div>
      )}

      {/* AI Coaching panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-4"
            data-testid="mgr-coaching">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-purple-600"/>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              AI Coaching
            </div>
            {coaching && (
              <span className="text-[10px] text-slate-500">
                · {coaching.model_name} · confidence {(coaching.confidence * 100).toFixed(0)}%
                {coaching.cached && (
                  <span className="ml-1 rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 uppercase text-[9px] font-bold">cached</span>
                )}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {coaching && (
              <button onClick={() => generateCoaching(true)} disabled={coachLoading}
                      data-testid="mgr-coach-regenerate"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-50">
                <RefreshCw size={12}/> Regenerate
              </button>)}
            <button onClick={() => generateCoaching(false)} disabled={coachLoading}
                    data-testid="mgr-coach-generate"
                    className="inline-flex items-center gap-1 rounded-md bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-purple-700 disabled:opacity-50">
              {coachLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {coaching ? "Refresh" : "Generate Coaching"}
            </button>
          </div>
        </div>
        {!coaching && !coachLoading && (
          <p className="text-sm text-slate-600">
            No AI coaching yet. Click <b>Generate Coaching</b> for an interpretive summary of this scorecard.
            <br/>
            <span className="text-[11px] text-slate-500">
              Powered by Claude Sonnet 4.6. Advisory only.
            </span>
          </p>
        )}
        {coachLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 size={14} className="animate-spin"/> Generating coaching insight…
          </div>
        )}
        {coaching && (
          <div className="space-y-3" data-testid="mgr-coach-output">
            <div className="text-base font-semibold text-slate-900" data-testid="mgr-coach-headline">
              {coaching.headline}
            </div>
            {(coaching.warnings || []).length > 0 && (
              <div className="text-[11px] text-amber-700 flex items-center gap-1">
                <AlertTriangle size={11}/> {coaching.warnings.length} item(s) filtered for compliance
              </div>
            )}
            <CoachSection title="Strengths" tone="emerald"
                            items={coaching.strengths} testid="strengths"/>
            <CoachSection title="Coaching Opportunities" tone="amber"
                            items={coaching.coaching_opportunities} testid="opportunities"/>
            <CoachSection title="Recommended Actions" tone="sky"
                            items={coaching.recommended_actions} testid="actions"/>
            {/* Feedback bar */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="text-[11px] text-slate-500">Was this coaching useful?</div>
              <div className="flex gap-2">
                <button onClick={() => sendCoachFeedback("helpful")}
                        data-testid="mgr-coach-fb-helpful"
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-1 hover:bg-emerald-100">
                  <ThumbsUp size={11}/> Helpful
                </button>
                <button onClick={() => sendCoachFeedback("not_helpful")}
                        data-testid="mgr-coach-fb-nothelp"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-1 hover:bg-slate-100">
                  Not helpful
                </button>
                <button onClick={() => sendCoachFeedback("incorrect")}
                        data-testid="mgr-coach-fb-incorrect"
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 text-red-700 text-xs font-semibold px-2 py-1 hover:bg-red-100">
                  <ThumbsDown size={11}/> Incorrect
                </button>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 italic pt-1">
              {coaching.disclaimer}
            </div>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 1 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="mgr-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Score History
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left py-1">Version</th>
                <th className="text-left py-1">Score</th>
                <th className="text-left py-1">Band</th>
                <th className="text-left py-1">Generated</th>
                <th className="text-left py-1">Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 10).map((h) => (
                <tr key={h.snapshot_id || h.id} className="border-t border-slate-100">
                  <td className="py-1">v{h.version}</td>
                  <td className="py-1 tabular-nums font-semibold">{h.overall_score.toFixed(1)}</td>
                  <td className="py-1">{h.band_label}</td>
                  <td className="py-1 text-slate-500">
                    {new Date(h.generated_at).toLocaleString()}
                  </td>
                  <td className="py-1 text-slate-500">{h.recalc_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Disclaimer */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500 flex items-start gap-2"
            data-testid="mgr-disclaimer">
        <Target size={12} className="mt-0.5 flex-shrink-0"/>
        <i>{card.disclaimer}</i>
      </div>
    </div>);
}


function FunnelRow({ label, value, reference }) {
  const v = value == null ? 0 : Number(value);
  const ref = reference == null || reference === 0 ? 1 : Number(reference);
  const pct = Math.max(2, Math.min(100, (v / ref) * 100));
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-1">
      <div className="col-span-4 text-xs text-slate-800">{label}</div>
      <div className="col-span-6">
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-sky-500" style={{ width: `${pct}%` }}/>
        </div>
      </div>
      <div className="col-span-2 text-right tabular-nums text-sm font-semibold">
        {value == null ? "—" : value}
      </div>
    </div>);
}


function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>);
}


const TONE_TW = {
  emerald: { dot: "bg-emerald-500", text: "text-emerald-700" },
  amber:   { dot: "bg-amber-500",   text: "text-amber-700" },
  sky:     { dot: "bg-sky-500",     text: "text-sky-700" },
};


const TREND_TONE = {
  improved: { icon: ArrowUp,   bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-700",   label: "improved" },
  declined: { icon: ArrowDown, bg: "bg-red-50",     ring: "ring-red-200",     text: "text-red-700",       label: "declined" },
  flat:     { icon: Minus,     bg: "bg-slate-50",   ring: "ring-slate-200",   text: "text-slate-600",     label: "flat" },
  unknown:  { icon: Minus,     bg: "bg-slate-50",   ring: "ring-slate-200",   text: "text-slate-400",     label: "n/a" },
};


function _fmtValue(v, format) {
  if (v == null) return "—";
  switch (format) {
    case "pct01":   return `${(v * 100).toFixed(1)}%`;
    case "money":   return `$${Number(v).toLocaleString()}`;
    case "minutes": return `${Math.round(v)} min`;
    case "int":
    default:        return Number.isInteger(v) ? v : v.toFixed(2);
  }
}


function TrendRow({ item }) {
  const t = TREND_TONE[item.tone] || TREND_TONE.unknown;
  const Icon = t.icon;
  const pct = item.delta_pct != null
    ? `${item.delta_pct > 0 ? "+" : ""}${(item.delta_pct * 100).toFixed(1)}%`
    : "—";
  return (
    <div className={`flex items-center gap-3 rounded-lg ${t.bg} ring-1 ${t.ring} px-3 py-2`}
          data-testid={`mgr-trend-${item.key}`}>
      <Icon size={14} className={t.text}/>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-800 truncate">{item.label}</div>
        <div className="text-[10px] text-slate-500">
          {_fmtValue(item.prior, item.format)} → {_fmtValue(item.current, item.format)}
        </div>
      </div>
      <div className={`text-sm font-semibold tabular-nums ${t.text}`}>
        {pct}
      </div>
    </div>);
}


function CoachSection({ title, tone, items, testid }) {
  if (!items || items.length === 0) return null;
  const t = TONE_TW[tone] || TONE_TW.sky;
  return (
    <div data-testid={`mgr-coach-${testid}`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${t.text} mb-1`}>
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => {
          const txt = typeof it === "string" ? it : it?.text || "";
          const paths = typeof it === "string" ? [] : it?.evidence_paths || [];
          return (
            <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
              <span className={`mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full ${t.dot}`}/>
              <div>
                <div>{txt}</div>
                {paths.length > 0 && (
                  <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] text-slate-500">
                    {paths.map((p, j) => (
                      <code key={j} className="rounded bg-slate-100 px-1 py-0.5">{p}</code>
                    ))}
                  </div>
                )}
              </div>
            </li>);
        })}
      </ul>
    </div>);
}
